import React, { useState, useEffect, useMemo } from 'react';
import { Box, Heading, Text, Image, VStack, HStack } from '@chakra-ui/react';
import tinycolor from 'tinycolor2';
import { Helmet } from 'react-helmet';
import { Layout } from '../templates';
import { CustomText } from '../atoms';
import { BannerWithSearchBar } from '../organisms/BannerWithSearchBar';
import { NewsArticle } from '../organisms/NewsSection';

interface NewsDetailPreviewProps {
  newsId: string;
  newsArticles?: NewsArticle[];
  settings?: any;
  onContactUsClick: () => void;
  onClickMyTickets: () => void;
  onClickLandingPageButton: () => void;
}

export const NewsDetailPreview: React.FC<NewsDetailPreviewProps> = ({
  newsId,
  newsArticles,
  settings,
  onContactUsClick,
  onClickMyTickets,
  onClickLandingPageButton,
}) => {
  const allArticles = useMemo(() => {
    return (newsArticles || []).map(article => ({
      ...article,

      enable: article.enable === 'true' || (article.enable as any) === true,
    }));
  }, [newsArticles]);

  const publishedArticles = useMemo(() => {
    return allArticles
      .filter(a => a.enable)
      .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
  }, [allArticles]);

  const [selectedArticle, setSelectedArticle] = useState<(typeof allArticles)[number] | null>(
    () => allArticles.find(a => a.id === newsId) ?? publishedArticles[0] ?? null,
  );

  useEffect(() => {
    const article = allArticles.find(a => a.id === newsId);
    if (article) {
      setSelectedArticle(article);
    }
  }, [newsId, allArticles]);

  const handleTitleClick = (article: (typeof allArticles)[number]) => {
    setSelectedArticle(article);
  };

  return (
    <Box bgColor={settings?.configs.secondaryBgColor}>
      <Helmet>
        <title>FEATURED</title>
      </Helmet>
      <Layout
        onClickLandingPageButton={onClickLandingPageButton}
        onClickMyTickets={onClickMyTickets}
        onContactUsClick={onContactUsClick}
        pageBgColor={settings?.configs.secondaryBgColor}
        settings={settings}
        isPreviewMode
      >
        <BannerWithSearchBar
          isFullScreen={settings?.configs.isBannerFullScreen}
          px={{
            base: '0vw',
            md: '0vw',
            lg: settings?.configs.isBannerFullScreen ? '0vw' : '7vw',
          }}
          imageSrc={settings?.configs.bannerImageUrl}
          altText="Contact Us Banner"
          showSearchBar={false}
          bannerTextColor="white"
          placeholderText=""
          showOverlay={undefined}
          showButton
          buttonText="Back"
          secondaryAccentColor={settings?.configs.secondaryAccentColor}
          bgColor={settings?.configs.secondaryBgColor}
          showJaggedBorder={settings?.configs.showJaggedBorder}
          placeholderFontSize={{ base: '20px', md: '22px', lg: '24px' }} // Responsive font size
          placeholderFontWeight="bold"
          isPreview
        />

        <HStack
          align="start"
          bg={settings?.configs?.secondaryBgColor}
          spacing={8}
          p={16}
          flexDir={{ base: 'column', md: 'row' }}
        >
          {/* Sidebar */}
          <VStack
            align="start"
            spacing={4}
            w={{ base: '100%', md: '30%' }}
            borderRight={{ md: '1px solid lightgray' }}
            pr={{ md: 4 }}
          >
            <CustomText fontWeight="bold" variant="h1">
              News Index
            </CustomText>
            {publishedArticles.map(article => (
              <Text
                key={article.id}
                onClick={() => handleTitleClick(article)}
                cursor="pointer"
                fontWeight={selectedArticle?.id === article.id ? 'bold' : 'normal'}
                _hover={{
                  textDecoration: 'underline',
                  color: tinycolor(settings?.configs?.secondaryAccentColor).toString(),
                }}
              >
                {article.title}
              </Text>
            ))}
          </VStack>

          {/* Main content */}
          <Box w={{ base: '100%', md: '70%' }}>
            {selectedArticle ? (
              <>
                <Heading size="xl" mb={4}>
                  {selectedArticle.title || 'Untitled Article'}
                </Heading>
                <HStack spacing={2} mb={4}>
                  {selectedArticle.writtenBy && (
                    <>
                      <Text fontSize="sm" color="gray.500">
                        Written By: {selectedArticle.writtenBy}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        |
                      </Text>
                    </>
                  )}
                  <Text fontSize="sm" color="gray.500">
                    Published on:{' '}
                    {
                      new Date(selectedArticle.createdDate).toLocaleDateString('en-US', {
                        month: 'long', // Aug
                        day: '2-digit', // 19
                        year: 'numeric', // 2025
                      })
                      // .replace(/(\w+)\s(\d{2}),\s(\d{4})/, '$1-$2-$3')
                    }
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    |
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Last Updated on:{' '}
                    {
                      new Date(selectedArticle.updatedDate).toLocaleDateString('en-US', {
                        month: 'long', // Aug
                        day: '2-digit', // 19
                        year: 'numeric', // 2025
                      })
                      // .replace(/(\w+)\s(\d{2}),\s(\d{4})/, '$1-$2-$3')
                    }
                  </Text>
                </HStack>

                {selectedArticle.imageUrl && (
                  <Image
                    src={selectedArticle.imageUrl}
                    alt={selectedArticle.title}
                    borderRadius="md"
                    mb={6}
                    height="auto"
                    maxHeight="500px"
                    objectFit="cover"
                    alignSelf="flex-start"
                  />
                )}

                <Box
                  fontSize="md"
                  lineHeight="medium"
                  sx={{
                    h1: { fontSize: '2em' },
                    h2: { fontSize: '1.5em' },
                    h3: { fontSize: '1.17em' },
                    h4: { fontSize: '1em' },
                    h5: { fontSize: '0.83em' },
                    h6: { fontSize: '0.67em' },
                    p: { fontSize: '1em', marginTop: -1, marginBottom: -1 },
                    ul: {
                      pl: '3em',
                    },
                    ol: {
                      pl: '3em',
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedArticle.content || '' }}
                />
              </>
            ) : (
              <Text>No article found.</Text>
            )}
          </Box>
        </HStack>
      </Layout>
    </Box>
  );
};
